/**
 * PATH: packages/ui/src/components/settings/role-management-tab/role-management-types.ts
 * PURPOSE: Shared types and constants for the RoleManagementTab component tree
 *
 * WHY: Extracted from RoleManagementTab.tsx to keep the main file under 300 lines
 *      and allow sub-components to import types without circular dependencies.
 *
 * DEPENDENCIES:
 *   - lucide-react: Icons mapped to each RBAC role
 *   - ../types: SettingsApiClient contract
 *   - ../../SettingsModal: SettingsUser type
 */

import {
  Crown,
  Shield,
  Calculator,
  Briefcase,
  FileCheck,
  HardHat,
  ShieldCheck,
  Users,
  User,
  ExternalLink,
} from 'lucide-react'
import type { SettingsApiClient } from '../types'
import type { SettingsUser } from '../../SettingsModal'

// =============================================================================
// TYPES
// =============================================================================

export interface SystemRole {
  code: string
  name: string
  description: string
  hierarchy_level: number
  is_customizable: boolean
  type: 'system' | 'custom'
}

export interface MemberRole {
  user_id: number
  email: string
  first_name: string
  last_name: string
  role: {
    code: string
    name: string
    type: 'system' | 'custom'
  } | null
  assigned_at: string
  assigned_by: string | null
}

export interface RoleManagementTabProps {
  apiClient?: SettingsApiClient
  organisationId?: string
  user?: SettingsUser | null
  className?: string
}

export interface RolesState {
  system_roles: SystemRole[]
  custom_roles: SystemRole[]
}

// =============================================================================
// ROLE DISPLAY CONSTANTS
// =============================================================================

export const ROLE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  org_owner: Crown,
  org_admin: Shield,
  finance_manager: Calculator,
  project_manager: Briefcase,
  estimator: FileCheck,
  site_supervisor: HardHat,
  qa_lead: ShieldCheck,
  hr_officer: Users,
  member: User,
  external: ExternalLink,
}

export const ROLE_COLORS: Record<string, string> = {
  org_owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  org_admin: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  finance_manager: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  project_manager: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  estimator: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
  site_supervisor: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  qa_lead: 'bg-teal-500/10 text-teal-600 border-teal-500/20',
  hr_officer: 'bg-pink-500/10 text-pink-600 border-pink-500/20',
  member: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  external: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
}

// =============================================================================
// ROLE CATEGORIES — derived from hierarchy_level ranges
// =============================================================================

export interface RoleCategory {
  label: string
  minLevel: number
  maxLevel: number
}

export const ROLE_CATEGORIES: RoleCategory[] = [
  { label: 'Leadership',        minLevel: 0,   maxLevel: 15 },
  { label: 'Administration',    minLevel: 16,  maxLevel: 25 },
  { label: 'Management',        minLevel: 26,  maxLevel: 35 },
  { label: 'Specialist',        minLevel: 36,  maxLevel: 49 },
  { label: 'Supervisor',        minLevel: 50,  maxLevel: 59 },
  { label: 'Office & Support',  minLevel: 60,  maxLevel: 75 },
  { label: 'Field & Operations', minLevel: 76, maxLevel: 99 },
  { label: 'General & External', minLevel: 100, maxLevel: 1000 },
]

export function groupRolesByCategory(roles: SystemRole[]): { label: string; roles: SystemRole[] }[] {
  const sorted = [...roles].sort((a, b) => a.hierarchy_level - b.hierarchy_level)
  const groups: { label: string; roles: SystemRole[] }[] = []

  for (const cat of ROLE_CATEGORIES) {
    const matching = sorted.filter(
      r => r.hierarchy_level >= cat.minLevel && r.hierarchy_level <= cat.maxLevel
    )
    if (matching.length > 0) {
      groups.push({ label: cat.label, roles: matching })
    }
  }

  return groups
}
